export const cropImage = async (blob: Blob, area: { x: number; y: number; width: number; height: number }) => {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const objectURL = URL.createObjectURL(blob)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(objectURL)
      resolve(image)
    }
    image.onerror = (event) => {
      const errorDetails = {
        blobType: blob.type || 'unknown',
        blobSize: blob.size,
        area: area,
        objectURL: objectURL,
        errorEvent: typeof event === 'object' && event !== null ? event.type : 'error',
      }
      const errorMessage = `Failed to load image: ${JSON.stringify(errorDetails)}`
      URL.revokeObjectURL(objectURL)
      reject(new Error(errorMessage))
    }
    image.src = objectURL
  })

  const w = 1280
  const h = 1280

  const canvas = document.createElement('canvas')
  canvas.height = h
  canvas.width = w
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Cannot get context from canvas')

  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, w, h)

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error('Failed to create blob from canvas'))
    }, blob.type)
  })
}

export const fileToDataURL = (file: File) => {
  return new Promise<string>((resolve, reject) => {
    try {
      const reader = new FileReader()
      reader.onload = function (event) {
        if (event.target && event.target.result && typeof event.target.result === 'string') {
          resolve(event.target.result)
          return
        }
        reject('onload is completed but cannot generate dataURL')
      }
      reader.readAsDataURL(file)
    } catch (e) {
      reject(e)
    }
  })
}

export const ALLOWED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'gif', 'png'] as const
type AllowedImageExtension = (typeof ALLOWED_IMAGE_EXTENSIONS)[number]

export const isValidImageFile = async (file?: File) => {
  // fileがundefinedの場合
  if (!file) return false
  const extension = getExtension(file.name)
  // そもそも許可されていない拡張子の場合
  if (!isAllowedImageExtension(extension)) return false

  const fileSlice = file.slice(0, 4)
  // safari13以下の場合arrayBuffer()が定義されてい場合がある
  // なので13以下の場合はクライアントでは検証しないことにした。
  if (typeof fileSlice.arrayBuffer !== 'function') return true

  // ファイルの中身を取得する（先頭4バイトのみ)
  const buffer = await fileSlice.arrayBuffer()
  const uint8Array = new Uint8Array(buffer)

  // ファイルの先頭の形式を検証する
  // 一つでもパターンに一致しないバイトがあればfalse
  const isValid = !IMAGE_BYTE_HEADING_PATTERN[extension].some((pattern, i) => uint8Array[i] !== pattern)
  return isValid
}

const getExtension = (fileName: string) => {
  const extension = fileName.substr(fileName.lastIndexOf('.') + 1).toLowerCase()
  return extension
}

const isAllowedImageExtension = (extension: string): extension is AllowedImageExtension => {
  return ALLOWED_IMAGE_EXTENSIONS.includes(extension as AllowedImageExtension)
}

// 拡張子ごとのファイルの先頭のバイト列
const IMAGE_BYTE_HEADING_PATTERN = {
  jpg: [0xff, 0xd8],
  jpeg: [0xff, 0xd8],
  png: [0x89, 0x50, 0x4e, 0x47],
  gif: [0x47, 0x49, 0x46, 0x38],
} as const
